import { AxiosRequestConfig } from 'axios';
import { generateSigningKey } from 'src/services/generateSigningKey';
import { generateEncryptedTotp, hashSecret } from './getTotp';
import { createRequestHash } from '../../services/requestHashService';
import { getPathOnly } from '../getPathOnly';
import { isMyWayStudioServiceReq, isProxyServerRequest } from './middlewareHelpers';
import { uint8ArrayToWordArray } from './buildLoginHeaders';

interface BuildProxyOptions {
    indexerR: string;
    accessToken: string;
    tokenType: string;
}

interface BuildProxyResult {
    headers: {
        Authorization: string;
        'X-TOTP': string;
        'X-Request-Signature': string;
    } | null;
    error: Error | null;
}

function extractPath(config: AxiosRequestConfig, treatAsMyWayStudioService = false): string {
    const isMyWayStudio = (isMyWayStudioServiceReq(config.url) || treatAsMyWayStudioService)
        && !isProxyServerRequest(config.url);

    if (isMyWayStudio) {
        return getPathOnly(config.url);
    }

    if (config.data && typeof config.data === 'string') {
        const parsed = new URLSearchParams(config.data);
        return parsed.get('webservice') ?? parsed.get('webService') ?? '';
    }

    return '';
}

export const buildProxyHeaders = async(
    config: AxiosRequestConfig,
    { indexerR, accessToken, tokenType }: BuildProxyOptions,
    treatAsMyWayStudioService = false,
): Promise<BuildProxyResult> => {
    const keyBytes = generateSigningKey(indexerR, accessToken);

    const totpResult = await generateEncryptedTotp(null, keyBytes);

    if (!totpResult.success) {
        return {
            headers: null,
            error: new Error(totpResult.error ?? 'TOTP generation failed'),
        };
    }

    const encryptedTotp = totpResult.data;

    const method = (config.method ?? 'GET').toUpperCase();
    const path = extractPath(config, treatAsMyWayStudioService);

    let body = '';
    if (typeof config.data === 'string') {
        body = config.data;
    } else if (config.data) {
        body = JSON.stringify(config.data);
    }

    const keyBytesArr = await hashSecret(keyBytes);
    const reqHash = createRequestHash(
        uint8ArrayToWordArray(keyBytesArr),
        method,
        path,
        body,
        ''
    );

    return {
        headers: {
            Authorization: `${tokenType} ${accessToken}`,
            'X-TOTP': encryptedTotp,
            'X-Request-Signature': reqHash,
        },
        error: null,
    };
}